import {
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';

export interface SelectOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-custom-select-input',
  imports: [CommonModule],
  templateUrl: './custom-select-input.component.html',
  styleUrl: './custom-select-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomSelectInputComponent),
      multi: true,
    },
  ],
})
export class CustomSelectInputComponent implements ControlValueAccessor, OnDestroy {
  @Input() options: SelectOption[] = [];
  @Input() placeholder = 'Select an option';
  @Input() searchable = false;
  @Input() searchPlaceholder = 'Search...';

  isOpen = false;
  selectedValue: string = '';
  isDisabled = false;
  searchTerm = '';

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private elRef: ElementRef) {}

  get selectedLabel(): string {
    return this.options.find(o => o.value === this.selectedValue)?.label ?? '';
  }

  get filteredOptions(): SelectOption[] {
    if (!this.searchable || !this.searchTerm.trim()) return this.options;
    const term = this.searchTerm.trim().toLowerCase();
    return this.options.filter(o => o.label.toLowerCase().includes(term));
  }

  toggle(): void {
    if (this.isDisabled) return;
    this.isOpen = !this.isOpen;
    if (!this.isOpen) {
      this.onTouched();
      this.searchTerm = '';
    }
  }

  onSearchTermChange(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement).value;
  }

  select(option: SelectOption): void {
    this.selectedValue = option.value;
    this.onChange(option.value);
    this.onTouched();
    this.isOpen = false;
    this.searchTerm = '';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elRef.nativeElement.contains(event.target)) {
      if (this.isOpen) {
        this.isOpen = false;
        this.onTouched();
        this.searchTerm = '';
      }
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) {
      this.isOpen = false;
      this.onTouched();
      this.searchTerm = '';
    }
  }

  writeValue(value: string): void {
    this.selectedValue = value ?? '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  ngOnDestroy(): void {
    this.isOpen = false;
  }
}
